import { useScreenBreakpoint } from 'consts/breakpoints';
import { Table } from 'ds/components/table';
import { useLocale } from 'locale';
import React from 'react';
import { MarketplaceType } from 'utils/entities';
import * as Styled from './styled';
import { TableColumn } from 'ds/components/table';
import { noop } from 'lodash';
import { connect } from 'react-redux';
import { State } from 'store/state';
import { safeDealsAgentsByIdsSelector } from 'store/state/domainData/selectors';
import { IGetPublicRealEstateAgentsByIdsResponseItem } from 'store/sagas/apiService/types';
import { UiAgentOfficeDeal } from 'screens/AgentOfficePages/common/sections/deals/types';

enum Column {
  Date = 'date',
  Address = 'address',
  Price = 'price',
  PropertyType = 'propertyType',
  Rooms = 'rooms',
  Size = 'size',
  Floor = 'floor',
  AgentCol = 'agentCol',
  Side = 'side',
}

interface DealsTableProps {
  data: UiAgentOfficeDeal[];
  dealsAgentsData?: IGetPublicRealEstateAgentsByIdsResponseItem[];
}

const BaseDealsTable: React.FC<DealsTableProps> = ({ data, dealsAgentsData }) => {
  const isMobile = useScreenBreakpoint() === 1;
  const { t, formatDate, formatMoney } = useLocale();

  const handleRowClick = (item: UiAgentOfficeDeal, _: number) => {
    window.open(`/sold/${item.saleId}`);
  };

  const getAddressStr = (item: UiAgentOfficeDeal) => `${item.structuredAddress.streetName} ${item.structuredAddress.streetNumber}, ${item.structuredAddress.city}`;

  const dateCol = {
    label: t('officePage.deals.columnTitle', { column: Column.Date }),
    renderFn: (item: UiAgentOfficeDeal) => (
      <Styled.Column>{formatDate(item.date)}</Styled.Column>
    ),
    sortFn: (items: UiAgentOfficeDeal[]) => items.sort((a, b) => +new Date(b.date) - +new Date(a.date)),
    width: '17%',
    gap: 16,
  } as TableColumn<UiAgentOfficeDeal>;

  const addressCol = {
    label: t('officePage.deals.columnTitle', { column: Column.Address }),
    renderFn: (item: UiAgentOfficeDeal) => (
      <Styled.Column>{getAddressStr(item)}</Styled.Column>
    ),
    sortFn: (items: UiAgentOfficeDeal[]) => {
      return items.sort((a, b) => {
        const address = getAddressStr(b);
        const addressCompare = getAddressStr(a);
        return addressCompare && address ? address.localeCompare(addressCompare) : -1;
      });
    },
    width: '20%',
    minWidth: '20%',
    gap: 5,
  } as TableColumn<UiAgentOfficeDeal>;

  const priceCol = {
    label: t('officePage.deals.columnTitle', { column: Column.Price }),
    renderFn: (item: UiAgentOfficeDeal) => (
      <Styled.Column>{formatMoney(item.declaredPrice)}</Styled.Column>
    ),
    sortFn: (items: UiAgentOfficeDeal[]) => items.sort((a, b) => b.declaredPrice - a.declaredPrice),
    width: '15%',
    gap: 16,
    align: 'right',
  } as TableColumn<UiAgentOfficeDeal>;

  const propertyTypeCol = {
    label: t('officePage.deals.columnTitle', { column: Column.PropertyType }),
    renderFn: (item: UiAgentOfficeDeal) => (
      <Styled.Column>{t('agent.office.propertyType', { propertyType: item.propertyType, marketPlace: MarketplaceType.Commercial })}</Styled.Column>
    ),
    sortFn: (items: UiAgentOfficeDeal[]) => items.sort((a, b) => a.propertyType && b.propertyType ? b.propertyType.localeCompare(a.propertyType) : -1),
    width: '15%',
    gap: 16,
    align: 'right',
  } as TableColumn<UiAgentOfficeDeal>;

  const roomsCol = {
    label: t('officePage.deals.columnTitle', { column: Column.Rooms }),
    renderFn: (item: UiAgentOfficeDeal) => (
      <Styled.Column>{item.numRooms}</Styled.Column>
    ),
    sortFn: (items: UiAgentOfficeDeal[]) => items.sort((a, b) => b.numRooms - a.numRooms),
    width: '15%',
    gap: 16,
    align: 'right',
  } as TableColumn<UiAgentOfficeDeal>;

  const sizeCol = {
    label: t('officePage.deals.columnTitle', { column: Column.Size }),
    renderFn: (item: UiAgentOfficeDeal) => (
      <Styled.Column>{item.grossArea}</Styled.Column>
    ),
    sortFn: (items: UiAgentOfficeDeal[]) => items.sort((a, b) => +b.grossArea - +a.grossArea),
    width: '15%',
    gap: 16,
    align: 'right',
  } as TableColumn<UiAgentOfficeDeal>;

  const floorCol = {
    label: t('officePage.deals.columnTitle', { column: Column.Floor }),
    renderFn: (item: UiAgentOfficeDeal) => (
      <Styled.Column>{item.floor}</Styled.Column>
    ),
    sortFn: (items: UiAgentOfficeDeal[]) => items.sort((a, b) => b.floor - a.floor),
    width: '15%',
    gap: 16,
    align: 'right',
  } as TableColumn<UiAgentOfficeDeal>;

  const agentCol = {
    label: t('officePage.deals.columnTitle', { column: Column.AgentCol }),
    renderFn: (item: UiAgentOfficeDeal) => item.agents && item.agents.length ? (
      <Styled.Column>
        {item.agents[0]
          ? (dealsAgentsData
            ? dealsAgentsData.some(agent => agent.agentId === item.agents[0].agentId)
              ? item.agents[0].agentName
              : item.agents[0].officeName
            : item.agents[0].agentName)
          : '-'}
      </Styled.Column>
    ) : null,
    sortFn: (items: UiAgentOfficeDeal[]) => items.sort((a, b) => a.agents && a.agents.length && b.agents && b.agents.length && b.agents[0].agentName ? b.agents[0].agentName.localeCompare(a.agents[0].agentName) : -1),
    width: '20%',
    gap: 16,
    align: 'right',
  } as TableColumn<UiAgentOfficeDeal>;

  const sideCol = {
    label: t('officePage.deals.columnTitle', { column: Column.Side }),
    renderFn: (item: UiAgentOfficeDeal) => item.agents && item.agents.length ? (
      <Styled.Column>
        {t('dealsPage.dealsTable.column.affiliationType', { type: item.side })}
      </Styled.Column>
    ) : null,
    sortFn: (items: UiAgentOfficeDeal[]) => items.sort((a, b) => a.agents && a.agents.length && b.agents && b.agents.length && b.agents[0].agentName ? b.agents[0].agentName.localeCompare(a.agents[0].agentName) : -1),
    width: '15%',
    gap: 16,
    align: 'right',
  } as TableColumn<UiAgentOfficeDeal>;

  const dateColMobile = {
    label: t('officePage.deals.columnTitle', { column: Column.Date }),
    renderFn: (item: UiAgentOfficeDeal) => (
      <Styled.MobileDateColumn>
        <Styled.DatePriceRow>
          <Styled.MainMobileInfo weight="bold">{formatDate(item.date)}</Styled.MainMobileInfo>
          <Styled.MainMobileInfo weight="bold">{formatMoney(item.declaredPrice)}</Styled.MainMobileInfo>
        </Styled.DatePriceRow>
        <Styled.SecondaryMobileInfo>
          <Styled.MobileAddress>{getAddressStr(item)}</Styled.MobileAddress>
          {item.propertyType ? <Styled.ListingInfo>{t('agent.office.propertyType', { propertyType: item.propertyType, marketPlace: MarketplaceType.Commercial })}</Styled.ListingInfo> : null}
          {item.grossArea ? <Styled.ListingInfo>{t('widget.prices.size', { size: item.grossArea })}</Styled.ListingInfo> : null}
          {item.numRooms ? <Styled.ListingInfo>{t('widget.prices.rooms', { rooms: item.numRooms })}</Styled.ListingInfo> : null}
          {item.floor ? <Styled.ListingInfo>{t('widget.prices.floor', { floor: item.floor })}</Styled.ListingInfo> : null}
          {item.side ? <Styled.ListingInfo>{t('widget.prices.affiliationSide', { affiliationSide: item.side })}</Styled.ListingInfo> : null}
          {item.agents && item.agents.length ? <Styled.ListingAgentInfo weight="bold"><div>{item.agents[0].agentName}</div></Styled.ListingAgentInfo> : null}
        </Styled.SecondaryMobileInfo>
      </Styled.MobileDateColumn>
    ),
    sortFn: (items: UiAgentOfficeDeal[]) => items.sort((a, b) => +new Date(b.date) - +new Date(a.date)),
    width: '50%',
    gap: 0,
    align: 'left',
  } as TableColumn<UiAgentOfficeDeal>;

  const priceColMobile = {
    label: t('officePage.deals.columnTitle', { column: Column.Price }),
    renderFn: noop,
    sortFn: (items: UiAgentOfficeDeal[]) => items.sort((a, b) => b.declaredPrice - a.declaredPrice),
    width: '50%',
    gap: 0,
    align: 'right',
  } as TableColumn<UiAgentOfficeDeal>;

  const columns = isMobile
    ? [ dateColMobile, priceColMobile ]
    : [ dateCol, addressCol, priceCol, propertyTypeCol, roomsCol, sizeCol, floorCol, agentCol, sideCol ];

  return (
    <>
      <Table<UiAgentOfficeDeal>
        visibleRows={isMobile ? 8 : 10}
        data={data}
        columns={columns}
        paginationType="pages"
        alwaysShowExpandIcon={false}
        onRowClick={handleRowClick}
      />
      {data.length ? null : <Styled.EmptyResults>{t('table.noResults')}</Styled.EmptyResults>}
    </>
  );
};

const mapStateToProps = (state: State) => ({
  dealsAgentsData: safeDealsAgentsByIdsSelector(state),
});

export const DealsTable = connect(mapStateToProps)(BaseDealsTable);
